import { CurrencyPipe, registerLocaleData } from '@angular/common';
import {
	provideHttpClient,
	withFetch,
	withInterceptorsFromDi,
	withJsonpSupport,
} from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeNl from '@angular/common/locales/nl';
import {
	APP_ID,
	ApplicationConfig,
	importProvidersFrom,
	inject,
	provideAppInitializer,
	provideZoneChangeDetection,
} from '@angular/core';
import {
	provideClientHydration,
	withEventReplay,
	withIncrementalHydration,
} from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
	provideRouter,
	withInMemoryScrolling,
	withRouterConfig,
} from '@angular/router';

import { ApmModule } from '@elastic/apm-rum-angular';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { RecaptchaV3Module, ReCaptchaV3Service } from 'ng-recaptcha';
import { MarkdownService, provideMarkdown } from 'ngx-markdown';
import { provideToastr } from 'ngx-toastr';

import { APM_ENVIRONMENT, provideApmHandling } from '@valk-nx/apm/index';
import {
	ConfigService,
	provideConfiguration,
} from '@valk-nx/content/app/config/config.service';
import {
	ReducerProvider,
	reducerToken,
} from '@valk-nx/content/app/root.reducer';
import { environment } from '@valk-nx/content/environments/environment';
import {
	ASSETS_ROOT,
	createTranslateLoader,
	fallbackLanguage,
	USE_NATIVE_NAVIGATION,
} from '@valk-nx/core/lib/core';
import { PostalCodeFacade } from '@valk-nx/postal-code-store/lib/postal-code.facade';
import { RouterEffects } from '@valk-nx/router-store/router.effects';
import { GoogleTagManagerService } from '@valk-nx/services/google-tag-manager/src/lib/google-tag-manager.service';
import { SESSION_STORAGE } from '@valk-nx/storyblok/components/order-giftcard/src/lib/order-giftcard';
import {
	initializeStoryblokClient,
	SB_CLIENT_FACTORY,
} from '@valk-nx/storyblok-services/src/lib/services/storyblok.client';
import { TagManagerEffects } from '@valk-nx/storyblok-store/src/lib/store/tag-manager/tag-manager.effects';
import { TagManagerFacade } from '@valk-nx/storyblok-store/src/lib/store/tag-manager/tag-manager.facade';

import { routes } from './app.routes';

registerLocaleData(localeNl, 'nl');
registerLocaleData(localeDe, 'de');
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeEs, 'es');

export function initializeGTM(gtmInitService: GoogleTagManagerService) {
	return (): void => gtmInitService.init();
}

export const appConfig: ApplicationConfig = {
	providers: [
		provideAppInitializer(() => {
			const initializerFn = initializeGTM(
				inject(GoogleTagManagerService),
			);
			return initializerFn();
		}),
		provideAngularSvgIcon(),
		provideAnimations(),
		provideApmHandling(),
		provideClientHydration(withEventReplay(), withIncrementalHydration()),
		provideConfiguration(),
		provideHttpClient(
			withFetch(),
			withInterceptorsFromDi(),
			withJsonpSupport(),
		),
		provideMarkdown(),
		provideRouter(
			routes,
			withRouterConfig({ onSameUrlNavigation: 'reload' }),
			withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
		),
		provideStore(reducerToken),
		provideEffects(TagManagerEffects, RouterEffects),
		provideToastr(),
		provideTranslateService({
			fallbackLang: fallbackLanguage,
			loader: provideTranslateHttpLoader(
				createTranslateLoader(environment.assetsRoot),
			),
		}),
		provideZoneChangeDetection({ eventCoalescing: true }),
		importProvidersFrom(ApmModule, RecaptchaV3Module),
		CurrencyPipe,
		MarkdownService,
		PostalCodeFacade,
		ReCaptchaV3Service,
		ReducerProvider,
		TagManagerFacade,
		TranslateService,
		{ provide: APP_ID, useValue: 'valkContentApp' },
		{ provide: ASSETS_ROOT, useValue: environment.assetsRoot },
		{
			provide: APM_ENVIRONMENT,
			useFactory: () => ({
				environment: inject(ConfigService).environment,
				name: environment.name,
				origin: inject(ConfigService).host,
				production: environment.production,
				version: environment.version,
			}),
		},
		{ provide: SESSION_STORAGE, useValue: globalThis.sessionStorage },
		{ provide: USE_NATIVE_NAVIGATION, useValue: false },
		{ provide: SB_CLIENT_FACTORY, useValue: initializeStoryblokClient },
	],
};
